import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { WalletHelper } from '@blink/components/src/utils';
import { Errors } from '@blink/components/src/constants/errors';

import { useToast } from '@/shared/hooks/useToast';
import { Store } from '@/shared/store';
import {
    AGREEMENT_CONTRACT_ERROR,
    AGREEMENT_ROLE_ERROR,
    DEPOSIT_ERROR,
    INFO_ERROR,
    TOKEN_PRICE_ERROR,
} from '@/shared/store/errors/constants';
import { getAgreementsAction } from '@/shared/store/agreements/actions';
import {
    removeAgreementContractErrorAction,
    clearErrorsAction,
} from '@/shared/store/errors/actions';
import { ToastAction } from '@/shared/ui/Toast/Toast';
import agreements from '@/shared/store/agreements';

const excludedErrors = [AGREEMENT_ROLE_ERROR, TOKEN_PRICE_ERROR, DEPOSIT_ERROR, INFO_ERROR];
const DURATION = 60000;

export const useToastErrors = () => {
    const { toast } = useToast();
    const errors = useSelector((state: Store) => state.errors);
    const lenderAgreements = useSelector((state: Store) => state.agreements.lenderAgreements);
    const borrowerAgreements = useSelector((state: Store) => state.agreements.borrowerAgreements);
    const dispatch = useDispatch();

    const shownErrorsRef = useRef(new Set<string>());

    useEffect(() => {
        Object.keys(errors).forEach((key) => {
            errors[key].forEach((errorMessage: string) => {
                const uniqueKey = `${key}:${errorMessage}`;
                if (
                    shownErrorsRef.current.has(uniqueKey) ||
                    excludedErrors.includes(key) ||
                    !errorMessage
                ) {
                    return;
                }

                if (errorMessage?.includes(Errors.PERMISSION_DENIED)) {
                    return toast({
                        title: errorMessage,
                        description: 'Please clear your browser cookies and refresh the page.',
                        variant: 'error',
                        duration: Infinity,
                        onClose: () => {
                            shownErrorsRef.current.delete(uniqueKey);
                            dispatch(
                                clearErrorsAction({
                                    errorType: key,
                                }),
                            );
                        },
                    });
                }

                if (key === AGREEMENT_CONTRACT_ERROR) {
                    const allAgreements = [...lenderAgreements, ...borrowerAgreements];

                    const agreement = allAgreements.find(
                        (a) => a.address.toLowerCase() === errorMessage.toLowerCase(),
                    );
                    if (!agreement) return;

                    const shortName = WalletHelper.addressShortName(agreement.address);

                    const onRetry = () => {
                        dispatch(
                            removeAgreementContractErrorAction({ message: agreement.address }),
                        );
                        dispatch(
                            getAgreementsAction({
                                withoutCache: true,
                            }),
                        );
                        shownErrorsRef.current.delete(uniqueKey);
                    };

                    const onClose = () => {
                        dispatch(
                            removeAgreementContractErrorAction({ message: agreement.address }),
                        );
                        shownErrorsRef.current.delete(uniqueKey);
                    };

                    toast({
                        title: `Connection to ${agreement.title} contract failed`,
                        description: (
                            <>
                                We couldn't retrieve information from contract{' '}
                                <strong
                                    className='cursor-pointer'
                                    onClick={() =>
                                        window.open(
                                            `https://etherscan.io/address/${agreement.address}`,
                                            '_blank',
                                            'noopener,noreferrer',
                                        )
                                    }
                                >
                                    {shortName}
                                </strong>
                            </>
                        ),
                        action: (
                            <ToastAction altText='Try again' onClick={onRetry}>
                                Try again
                            </ToastAction>
                        ),
                        onClose,
                        duration: DURATION,
                        variant: 'error',
                    });
                } else {
                    toast({
                        title: errorMessage,
                        variant: 'error',
                    });
                }

                shownErrorsRef.current.add(uniqueKey);
            });
        });
    }, [errors, agreements, dispatch, toast]);
};
